import React, { useState } from 'react';
import SleekExplainField from './SleekExplainField';

const PassportForm: React.FC = () => {
  const [hasPassportBook, setHasPassportBook] = useState<string>('');
  const [passportDetails, setPassportDetails] = useState<string>('');
  const [hasPassportCard, setHasPassportCard] = useState<string>('');
  const [showExplanation, setShowExplanation] = useState<boolean>(false);

  const handlePassportBookChange = (value: string) => {
    setHasPassportBook(value);
    if (value === 'no') {
      setPassportDetails('');
    }
  };

  const handlePassportCardChange = (value: string) => {
    setHasPassportCard(value);
    if (value === 'unknown') {
      setShowExplanation(true);
    } else {
      setShowExplanation(false);
    }
  };

  return (
    <div className="flex-1 p-8 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-8">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">U.S. passport information</h2>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800">
              This section asks for information regarding your U.S. passport, if you have one. This 
              information is may also be used to verify citizenship for U.S. citizens and travel to non-U.S. 
              locations. If you have held a U.S. passport, passport card, or both, answer the questions 
              for this item exactly as they appear and provide the requested information. If you have 
              access to your most recent expired or valid passport, provide the information as best as 
              you can. If you have never held a passport, passport card, or both, answer the questions 
              as best as you can. For more information on U.S. passports, go to the U.S. 
              State Department web site — <a href="https://travel.state.gov/passport" className="text-blue-600 underline">https://travel.state.gov/passport</a>.
            </p>
          </div>
        </div>

        <div className="space-y-8">
          {/* Passport Book Question */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Have you ever held a U.S. passport book?
            </h3>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="passportBook"
                  value="yes"
                  checked={hasPassportBook === 'yes'}
                  onChange={(e) => handlePassportBookChange(e.target.value)}
                  className="h-4 w-4 text-gov-blue focus:ring-gov-blue border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">Yes</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="passportBook"
                  value="no"
                  checked={hasPassportBook === 'no'}
                  onChange={(e) => handlePassportBookChange(e.target.value)}
                  className="h-4 w-4 text-gov-blue focus:ring-gov-blue border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">No</span>
              </label>
            </div>

            {hasPassportBook === 'yes' && (
              <div className="mt-6">
                <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm font-medium">
                  Open Answer
                </button>
              </div>
            )}
          </div>

          {/* Passport Details */}
          {hasPassportBook === 'yes' && (
            <div>
              <label htmlFor="passportDetails" className="block text-sm font-medium text-gray-700 mb-2">
                Passport details:
              </label>
              <textarea
                id="passportDetails"
                value={passportDetails}
                onChange={(e) => setPassportDetails(e.target.value)}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-gov-blue focus:border-gov-blue"
                placeholder="Enter passport details..."
              />
            </div>
          )}

          {/* Passport Card Question */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Have you ever held a U.S. passport card?
            </h3>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="passportCard"
                  value="yes"
                  checked={hasPassportCard === 'yes'}
                  onChange={(e) => handlePassportCardChange(e.target.value)}
                  className="h-4 w-4 text-gov-blue focus:ring-gov-blue border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">Yes</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="passportCard"
                  value="no"
                  checked={hasPassportCard === 'no'}
                  onChange={(e) => handlePassportCardChange(e.target.value)}
                  className="h-4 w-4 text-gov-blue focus:ring-gov-blue border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">No</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="passportCard"
                  value="unknown"
                  checked={hasPassportCard === 'unknown'}
                  onChange={(e) => handlePassportCardChange(e.target.value)}
                  className="h-4 w-4 text-gov-blue focus:ring-gov-blue border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">I don't know</span>
              </label>
            </div>

            {hasPassportCard === 'yes' && (
              <div className="mt-6">
                <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm font-medium">
                  Open Answer
                </button>
              </div>
            )}
          </div>

          {/* Explanation Field with Sleek AI Assist */}
          {showExplanation && (
            <SleekExplainField
              label="Please explain:"
              placeholder="Please provide an explanation about your passport card status..."
              helpText="AI assistance available to help craft a comprehensive response"
              fieldType="passport-card-explanation"
            />
          )}
        </div>

        {/* Action Buttons */}
        <div className="mt-8 pt-6 border-t border-gray-200 flex justify-between">
          <button className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
            Previous
          </button>
          <div className="space-x-3">
            <button className="px-6 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200">
              Save Progress
            </button>
            <button className="px-6 py-2 bg-gov-blue text-white rounded-md hover:bg-blue-800">
              Next
            </button>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default PassportForm;